import React from 'react'

import Loading from '../../components/layout/Loading'
import DateRow from '../../components/layout/DateRow'
import SimpleVideoPlayer from '../../components/video/simplePlayer'
import PageHeading from '../../components/layout/PageHeading'
import Tag from '../../components/content/Tag'
import LocationInfo from '../../components/events/LocationInfo'
import ToggleRsvp from './ToggleRsvp'
import { useEvent } from './hooks'
import Button from '../../components/form/Button'
import GoogleCalendarIcon from '../../../assets/icons/google-calendar.svg?react'
import AppleCalendarIcon from '../../../assets/icons/apple-calendar.svg?react'
import * as dateLib from '../../../libraries/date'
import { useI18N } from '../../../libraries/i18n/hook'
import MoreActionsButton from '../../components/form/MoreActionsButton'
import ShareButton from '../../components/form/ShareButton'

type Props = { id: number }
const EventDetailsContainer: React.FC<Props> = ({ id }) => {
  const { data, isLoading } = useEvent(id)

  const { t } = useI18N()

  // Add redirect for past events
  React.useEffect(() => {
    if (data && new Date(data.endDate) < new Date()) {
      window.location.href = '/events/'
    }
  }, [data])

  if(!data || isLoading) { return <Loading size='fill-container' /> }

  const renderRsvpButton = (buttonSize: 'sm' | 'md') => (
    <ToggleRsvp eventId={data.id} size={buttonSize} />
  )

  const renderCalendarButton = (buttonSize: 'sm' | 'md', onClick: () => void, icon: React.ReactElement, label: string) => (
    <Button icon={icon} size={buttonSize} variant='flat' outlined onClick={onClick}>
      <span className='w-36 font-normal text-xs text-black'>{label}</span>
    </Button>
  )

  const moreActionsItems = (buttonSize: 'sm' | 'md') => ([
    {
      id: 'addToCalendar',
      item: (
        <span className='w-36 text-xs text-black'>{t('events.add.to.calendar')}</span>
      ),
      disabled: true
    },
    {
      id: 'googleCalendar',
      item: renderCalendarButton(buttonSize, () => window.open(`https://www.google.com/calendar/render?action=TEMPLATE&text=${data.title}&dates=${calendarStartDate}/${calendarEndDate}&details=${encodeURIComponent(`<p>${data.description}</p><a href="${window.location.href}">${window.location.href}</a>`)}`), <GoogleCalendarIcon />, 'Google Calendar'),
    },
    {
      id: 'appleCalendar',
      item: renderCalendarButton(buttonSize, () => window.open(dateLib.getICalFile(data.title, data.location ?? '', data.description, new Date(data.startDate), new Date(data.endDate), window.location.href)), <AppleCalendarIcon />, 'Apple Calendar'),
    },
  ])

  const renderTagType = () => (
    <Tag outlined border>{data.type.toUpperCase()}</Tag>
  )

  const renderActionsBar = (size: 'sm' | 'md') => (
    <div className='mt-4 w-full flex items-center gap-x-6'>
      {renderRsvpButton('sm')}
      <ShareButton title={data.title} />
      <div className='grow text-end'>
        <MoreActionsButton
          disabled={new Date(data.endDate) < new Date()}
          items={moreActionsItems(size)}
        />
      </div>
    </div>
  )

  const calendarStartDate = dateLib.getDateTimeInISO8601(data.startDate)
  const calendarEndDate = dateLib.getDateTimeInISO8601(data.endDate)

  return (
    <div>
      <PageHeading>{data.title}</PageHeading>
      <p className='text-xs sm:text-base font-light text-black'>{data.description}</p>
      <div className='mt-9' />
      <div className='rounded-lg shadow-md'>
        <DateRow startDate={data.startDate} endDate={data.endDate} fixedHeight={false}>
          <div className='flex items-end justify-between h-full sm:py-3'>
            <div className='w-full flex flex-col h-full gap-y-1 sm:gap-y-3'>
              <div className='flex'>
                <h5 className='flex-1 line-clamp-2 sm:line-clamp-3'>{data.title}</h5>
                <div className='hidden sm:flex h-full pe-2 pt-2'>
                  {renderTagType()}
                </div>
              </div>
              <p className='text-2xs sm:text-base font-light'>{data.authors.toString().replace(/,/g, ', ')}</p>
              <p className='text-2xs sm:text-base flex justify-between flex-row gap-x-2 items-center my-1 sm:my-2'>
                <div className='max-w-[80%]'><LocationInfo location={data.location} streamingLink={data.streamingLink} /></div>
                <div className='hidden sm:flex'>
                  {renderActionsBar('sm')}
                </div>
              </p>
              <div className='flex justify-between items-end sm:hidden'>
                {renderTagType()}
              </div>
            </div>
          </div>
        </DateRow>
      </div>

      <div className='flex sm:hidden'>
        {renderActionsBar('md')}
      </div>

      {data.streamingLink && (
        <div className='my-12'>
          <SimpleVideoPlayer id='liveStreamingPlayer' srcUrl={data.streamingLink} />
        </div>
      )}
    </div>
  )
}

export default EventDetailsContainer
